import { useState } from 'react';
import dayjs from 'dayjs';
import { FormProvider, useForm, type FieldErrors } from 'react-hook-form';
import { toast } from 'react-toastify';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { noAutoClose } from 'assets/data/config';
import { useCreateCustomEvent } from 'hooks/customEvents/useCustomEvents';
import { useCreateNotification } from 'hooks/useNotification';
import { parseError } from 'utils/parseTypedErrorMessage';
import AssetsAtRiskSection from './AssetsAtRiskSection';
import EventDetailsSection from './EventDetailsSection';
import type { EventFormValues } from './eventForm';
import {
  EVENT_NOTIFICATION_DEFAULTS,
  validateEventNotification,
  type EventNotificationFormValues,
} from './eventNotification';
import NotifyUsersSection from './NotifyUsersSection';
import ResponsePlanSection from './ResponsePlanSection';
import SectionedForm from './SectionedForm';

const DETAILS_SECTION = 0;
const ASSETS_SECTION = 1;
const RESPONSE_SECTION = 2;
const NOTIFY_SECTION = 3;
const FORM_ID = 'new-event-form';

type NewEventDialogProps = {
  onClose: () => void;
};

const NewEventDialog: React.FC<NewEventDialogProps> = ({ onClose }) => {
  const [activeSection, setActiveSection] = useState(DETAILS_SECTION);
  const [notifyUsers, setNotifyUsers] = useState(false);

  const form = useForm<EventFormValues>({
    defaultValues: {
      name: '',
      status: 'active',
      summary: '',
      description: '',
      aorIds: [],
      resourceSetIds: [],
      actions: [],
    },
  });
  const notificationForm = useForm<EventNotificationFormValues>({
    defaultValues: EVENT_NOTIFICATION_DEFAULTS,
  });
  const createEventMutation = useCreateCustomEvent();
  const createNotificationMutation = useCreateNotification();

  const isNotificationValid = () =>
    !notifyUsers || validateEventNotification(notificationForm);

  const onInvalid = (formErrors: FieldErrors<EventFormValues>) => {
    const notificationValid = isNotificationValid();
    const hasDetailsErrors =
      formErrors.name ||
      formErrors.category ||
      formErrors.severity ||
      formErrors.startDatetime ||
      formErrors.expectedEndDatetime;
    if (hasDetailsErrors) {
      setActiveSection(DETAILS_SECTION);
    } else if (formErrors.actions) {
      setActiveSection(RESPONSE_SECTION);
    } else if (!notificationValid) {
      setActiveSection(NOTIFY_SECTION);
    }
  };

  const createEvent = async (values: EventFormValues) => {
    try {
      await createEventMutation.mutateAsync({
        name: values.name,
        category: values.category,
        severity: values.severity,
        status: values.status,
        summary: values.summary.trim() || null,
        description: values.description.trim() || null,
        startDatetime: dayjs(values.startDatetime).toISOString(),
        expectedEndDatetime: values.expectedEndDatetime?.toISOString() ?? null,
        aorIds: values.aorIds,
        resourceSetIds: values.resourceSetIds,
        geometry: values.location?.geojson.data.features[0]?.geometry ?? null,
        actions: values.actions.map((action) => ({
          name: action.name,
          description: action.description.trim() || null,
          severity: action.severity ?? null,
          poc: action.poc ?? null,
        })),
      });
      return true;
    } catch (error) {
      toast.error(parseError(error) ?? 'Failed to create event.');
      return false;
    }
  };

  const onSubmit = async (values: EventFormValues) => {
    if (!isNotificationValid()) {
      setActiveSection(NOTIFY_SECTION);
      return;
    }
    const notification = notifyUsers
      ? { ...notificationForm.getValues() }
      : null;
    if (!(await createEvent(values))) {
      return;
    }
    if (notification) {
      try {
        await createNotificationMutation.mutateAsync({
          ...notification,
          incidentId: null,
          autoArchiveDate: null,
          phoneMessage: null,
          teamIds: [],
          sourceItemId: null,
          sourceItemType: null,
          scheduledDate: null,
          aors: values.aorIds,
        });
      } catch {
        toast.error(
          'The event was created, but the notification was not sent. Send it from the Notifications tab.',
          noAutoClose
        );
      }
    }
    onClose();
  };

  const isPending =
    createEventMutation.isPending || createNotificationMutation.isPending;

  return (
    <Dialog
      open={true}
      onOpenChange={(open) => !open && onClose()}
      disablePointerDismissal
    >
      <DialogContent className="tw:sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>New Event</DialogTitle>
        </DialogHeader>
        <FormProvider {...form}>
          <SectionedForm
            formId={FORM_ID}
            sections={[
              {
                id: DETAILS_SECTION,
                label: 'Event Details',
                content: <EventDetailsSection />,
              },
              {
                id: ASSETS_SECTION,
                label: 'Assets at Risk',
                content: <AssetsAtRiskSection />,
              },
              {
                id: RESPONSE_SECTION,
                label: 'Response Plan',
                content: <ResponsePlanSection />,
              },
              {
                id: NOTIFY_SECTION,
                label: 'Notify Users',
                content: (
                  <NotifyUsersSection
                    notifyUsers={notifyUsers}
                    onNotifyUsersChange={setNotifyUsers}
                    notificationForm={notificationForm}
                  />
                ),
              },
            ]}
            activeSectionId={activeSection}
            onSectionChange={setActiveSection}
            onSubmit={(e) => void form.handleSubmit(onSubmit, onInvalid)(e)}
          />
        </FormProvider>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form={FORM_ID} disabled={isPending}>
            Create Event
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default NewEventDialog;
